import { PRO_PLAYERS } from '@/components/trial/shared/pro-players';

export const DARK_BG = '#080809';
export const NOPING_GREEN = '#93FF18';
export const ACCENT_CYAN = '#00E5FF';
export const ACCENT_BLUE = '#2ECDFF';
export const TEXT_LIGHT = '#E5E7EB';
export const MUTED_GRAY = '#929298';
export const CARD_DARK = '#0a0a0f';

export const EASE_OUT: [number, number, number, number] = [0.22, 1, 0.36, 1];

export const HERO_CHART = {
  highPingPath: 'M0,20 L50,25 L80,15 L120,30 L150,18 L180,35 L200,22 L230,40 L250,28',
  dropPath: 'M250,28 C280,30 300,80 320,150 Q340,175 360,178 L400,180 L450,179 L500,180 L550,179 L600,180',
  fillPath: 'M250,28 C280,30 300,80 320,150 Q340,175 360,178 L400,180 L450,179 L500,180 L550,179 L600,180 L600,200 L250,200 Z',
} as const;

export const BEFORE_GAUGES = [
  { label: 'Ping', value: 180, max: 200, unit: 'ms', color: 'bg-red-500' },
  { label: 'Packet Loss', value: 15, max: 100, unit: '%', color: 'bg-orange-500' },
  { label: 'Jitter', value: 45, max: 60, unit: 'ms', color: 'bg-red-400' },
] as const;

export const AFTER_GAUGES = [
  { label: 'Ping', value: 9, max: 200, unit: 'ms', barColor: NOPING_GREEN },
  { label: 'Packet Loss', value: 0, max: 100, unit: '%', barColor: NOPING_GREEN },
  { label: 'Jitter', value: 2, max: 60, unit: 'ms', barColor: ACCENT_CYAN },
] as const;

export const INFRA_STATS = [
  { target: 3000000, suffix: '+', label: 'infra_stats.active_gamers.label', sublabel: 'infra_stats.active_gamers.sublabel' },
  { target: 2000, suffix: '+', label: 'infra_stats.global_servers.label', sublabel: 'infra_stats.global_servers.sublabel' },
  { target: 150, suffix: '+', label: 'infra_stats.countries.label', sublabel: 'infra_stats.countries.sublabel' },
  { target: 2000, suffix: '+', label: 'infra_stats.games_supported.label', sublabel: 'infra_stats.games_supported.sublabel' },
] as const;

export const HOW_IT_WORKS_STEPS = [
  {
    step: '01',
    titleKey: 'how_it_works.steps.0.title',
    descKey: 'how_it_works.steps.0.desc',
    iconPath: 'M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3',
  },
  {
    step: '02',
    titleKey: 'how_it_works.steps.1.title',
    descKey: 'how_it_works.steps.1.desc',
    iconPath: 'M15.042 21.672L13.684 16.6m0 0l-2.51 2.225.569-9.47 5.227 7.917-3.286-.672zm-7.518-.267A8.25 8.25 0 1120.25 10.5M8.288 14.212A5.25 5.25 0 1117.25 10.5',
  },
  {
    step: '03',
    titleKey: 'how_it_works.steps.2.title',
    descKey: 'how_it_works.steps.2.desc',
    iconPath: 'M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z',
  },
] as const;

export const TECH_FEATURES = [
  {
    titleKey: 'tech.features.0.title',
    descKey: 'tech.features.0.desc',
    metricKey: 'tech.features.0.metric',
    iconPath: 'M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5',
  },
  {
    titleKey: 'tech.features.1.title',
    descKey: 'tech.features.1.desc',
    metricKey: 'tech.features.1.metric',
    iconPath: 'M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z',
  },
  {
    titleKey: 'tech.features.2.title',
    descKey: 'tech.features.2.desc',
    metricKey: 'tech.features.2.metric',
    iconPath: 'M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z',
  },
] as const;

export const MULTI_ROUTES = [
  { city: 'Chicago 112', hops: ['CHI-POP', 'CHI-Core', 'US-MW1', 'US-Peer', 'GS-Link'], total: '18ms', active: false },
  { city: 'Dallas 108', hops: ['DAL-POP', 'DAL-Core', 'US-SC1', 'US-Hub', 'GS-Bkp'], total: '12ms', active: false },
  { city: 'Denver 95', hops: ['DEN-POP', 'DEN-Core', 'US-MT1', 'US-Bkb', 'GS-DEN'], total: '22ms', active: false },
  { city: 'Atlanta 101', hops: ['ATL-POP', 'ATL-Core', 'US-SE1', 'US-Edge', 'GS-ATL'], total: '15ms', active: false },
  { city: 'Seattle 90', hops: ['SEA-POP', 'SEA-Core', 'US-NW1', 'US-Fast', 'GS-SEA'], total: '8ms', active: true },
] as const;

export const FAQ_ITEMS_KEYS = [0, 1, 2, 3] as const;

export const PRO_ENDORSEMENTS = [
  { ...PRO_PLAYERS.sacy, quoteKey: 'pros.sacy.quote', ping: '8ms' },
  { ...PRO_PLAYERS.coldzera, quoteKey: 'pros.coldzera.quote', ping: '11ms' },
  { ...PRO_PLAYERS.brtt, quoteKey: 'pros.brtt.quote', ping: '6ms' },
] as const;

export const GAME_CAROUSEL_ITEMS = [
  { name: 'Valorant', src: '/images/Valorant.webp' },
  { name: 'CS2', src: '/images/CS2.webp' },
  { name: 'Fortnite', src: '/images/Fortnite.webp' },
  { name: 'Dota 2', src: '/images/Dota2.webp' },
  { name: 'World of Warcraft', src: '/images/World of Warcraft.webp' },
  { name: 'Final Fantasy XIV', src: '/images/Final Fantasy XIV.webp' },
  { name: 'Rainbow Six Siege', src: '/images/Rainbow Six Siege.png' },
  { name: 'Escape From Tarkov', src: '/images/Escape From Tarkov.webp' },
  { name: 'Ragnarok', src: '/images/Ragnarok.webp' },
  { name: 'Tibia', src: '/images/Tibia.png' },
  { name: 'Warface', src: '/images/Warface.png' },
  { name: 'StarCraft', src: '/images/StarCraft.webp' },
  { name: 'Albion', src: '/images/Albion.png' },
  { name: 'Lineage II', src: '/images/Lineage II.webp' },
] as const;
